import React, { useState, useContext, useEffect } from 'react';
import Header from '../components/Header';
import SearchResult from './SearchResult';
import '../style/body-style.css';
import '../style/search-box.css';
import CK_en from "../resources/CK_en.json";
import CK_fr from '../resources/CK_fr.json';
import LanguageContext from '../components/LanguageContext';
import Footer from '../components/Footer';
import DOMPurify from 'dompurify';
import {getBaseUrl} from "../components/Utils";

function Home() {
  const [hintExpanderOpen, setHintExpanderOpen] = useState({ name: false, ocn: false, bin: false });
  const [exactMatch, setExactMatch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchResultOpen, setSearchResultOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [dataResponse, setDataResponse] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const { language } = useContext(LanguageContext);
  const [searchError, setSearchError] = useState('');
  const [displayInenigible, setDisplayInenigible] = useState(false);

  const toggleHintExpander = (key) => {
    setHintExpanderOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const LoadingIndicator = () => (
    <div className="loading-indicator-container">
      <div className="ontario-loading-indicator">
        <svg className="ontario-loading-indicator__spinner" viewBox="25 25 50 50" xmlns="http://www.w3.org/2000/svg">
          <circle cx="50" cy="50" r="20" fill="none" />
        </svg>
        <p>Loading</p>
      </div>
    </div>
  );

  const handleExactMatchChange = (event) => {
    setExactMatch(event.target.checked);
  };

  const handleOnChangeText = (event) => {
    setSearchText(event.target.value);
    setSearchError('');
  };

  const CK = language === 'en' ? CK_en : CK_fr;
  document.title = CK['entity_search.title'];

  const handleOnSubmit = async (event) => {
    event.preventDefault();
    setDisplayInenigible(false);

    if (searchText.trim() === '') {
      setSearchError(CK["entity_search.error.null_search"]);
      return;
    }

    setIsLoading(true);

    // Clear any previous search results and search text from sessionStorage
    sessionStorage.removeItem('Home');
    sessionStorage.removeItem('searchText');

    const controller = new AbortController();
    const { signal } = controller;

    const fetchData = async () => {
        try {
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay for user experience
            const response = await fetch(getBaseUrl(process.env.REACT_APP_ENV) + '/searchEntities', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    searchFor: searchText,
                    exactMatch: exactMatch
                }),
                signal // Pass the signal to the fetch request
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            // Store the new search results and search text in sessionStorage
            sessionStorage.setItem('Home', JSON.stringify(data));
            sessionStorage.setItem('searchText', searchText);

            setDataResponse(data);
            setSearchResultOpen(true);
            } catch (error) {
                if (error.name === 'AbortError') {
                    console.log('Fetch request was aborted');
                } else {
                    console.error('Error fetching data:', error);
                    setSearchResultOpen(true);
                }
            } finally {
                setIsLoading(false);
            }
        };

        const timeout = new Promise((_, reject) => {
            setTimeout(() => {
                controller.abort(); // Abort the fetch request
                reject(new Error('The request is taking longer than expected.'));
            }, 30000); // 30 seconds
        });

        try {
            await Promise.race([fetchData(), timeout]);
        } catch (error) {
            console.error(error.message);
            setIsLoading(false);
            setSearchResultOpen(false);
            alert('The request is taking longer than expected. Please try again later.');
        }
    };

  const handleReset = () => {
    setDisplayInenigible(false);
    setSearchText('');
    setSearchError('');
    setExactMatch(false);
    setDataResponse([]);
    setSearchResultOpen(false);

    // Clear the search results and search text from sessionStorage when the form is reset
    sessionStorage.removeItem('Home');
    sessionStorage.removeItem('searchText');
  };

  useEffect(() => {
    const storedResults = sessionStorage.getItem('Home');
    const storedSearchText = sessionStorage.getItem('searchText');
    if (storedResults) {
        setDataResponse(JSON.parse(storedResults));
        setSearchResultOpen(true);
    }
    if (storedSearchText) {
        setSearchText(storedSearchText);
    }
    
    if (searchText.trim() === '' && searchError) {
      setSearchError(CK["entity_search.error.null_search"]);
    }
  }, [language]);


  const helpContent = CK["entity_search.options.name.help"];
  const sanitizedHtml = DOMPurify.sanitize(helpContent);

  const helpContent2 = CK["entity_search.options.ocn.help"];
  const sanitizedHtml2 = DOMPurify.sanitize(helpContent2);

  const helpContent3 = CK["entity_search.options.bin.help"];
  const sanitizedHtml3 = DOMPurify.sanitize(helpContent3);

  return (
    <div className="home-container">
      <Header />
      <div id="entity-search__container" className="ontario-container">
          <h1 className="ontario-h1" tabIndex={0}>
              {CK["entity_search.header"]}
          </h1>
        <p tabIndex={0}>
          {CK["entity_search.options.label"]}<strong>:</strong>
        </p>
        <div className={`ontario-hint-expander__container${hintExpanderOpen.name ? ' ontario-expander--active' : ''}`}>
          <button className="ontario-hint-expander__button" id="hint-expander-btn-name" aria-controls="hint-expander-content-name" aria-expanded={hintExpanderOpen.name} onClick={() => toggleHintExpander('name')}>
            <span className="ontario-hint-expander__button-icon--close">
              <svg className="ontario-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M7.4 15.4l4.6-4.58 4.6 4.58L18 14l-6-6-6 6 1.4 1.4z" /></svg>
            </span>
            <span className="ontario-hint-expander__button-icon--open">
              <svg className="ontario-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M7.4 8.6l4.6 4.58 4.6-4.58L18 10l-6 6-6-6 1.4-1.4z" /></svg>
            </span>
            {CK["entity_search.options.name"]}
          </button>
          <div className={`ontario-hint-expander__content${hintExpanderOpen.name ? ' ontario-expander__content--opened' : ''}`} id="hint-expander-content-name" aria-labelledby="hint-expander-btn-name" aria-hidden={!hintExpanderOpen.name}>
            <ul dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
          </div>
        </div>
        <div className={`ontario-hint-expander__container${hintExpanderOpen.ocn ? ' ontario-expander--active' : ''}`}>
          <button className="ontario-hint-expander__button" id="hint-expander-btn-ocn" aria-controls="hint-expander-content-ocn" aria-expanded={hintExpanderOpen.ocn} onClick={() => toggleHintExpander('ocn')}>
            <span className="ontario-hint-expander__button-icon--close">
              <svg className="ontario-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M7.4 15.4l4.6-4.58 4.6 4.58L18 14l-6-6-6 6 1.4 1.4z" /></svg>
            </span>
            <span className="ontario-hint-expander__button-icon--open">
              <svg className="ontario-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M7.4 8.6l4.6 4.58 4.6-4.58L18 10l-6 6-6-6 1.4-1.4z" /></svg>
            </span>
            {CK["entity_search.options.ocn"]}
          </button>
          <div className={`ontario-hint-expander__content${hintExpanderOpen.ocn ? ' ontario-expander__content--opened' : ''}`} id="hint-expander-content-ocn" aria-labelledby="hint-expander-btn-ocn" aria-hidden={!hintExpanderOpen.ocn}>
            <ul dangerouslySetInnerHTML={{ __html: sanitizedHtml2 }} />
          </div>
        </div>
        <div className={`ontario-hint-expander__container${hintExpanderOpen.bin ? ' ontario-expander--active' : ''}`}>
          <button className="ontario-hint-expander__button" id="hint-expander-btn-bin" aria-controls="hint-expander-content-bin" aria-expanded={hintExpanderOpen.bin} onClick={() => toggleHintExpander('bin')}>
            <span className="ontario-hint-expander__button-icon--close">
              <svg className="ontario-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M7.4 15.4l4.6-4.58 4.6 4.58L18 14l-6-6-6 6 1.4 1.4z" /></svg>
            </span>
            <span className="ontario-hint-expander__button-icon--open">
              <svg className="ontario-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M7.4 8.6l4.6 4.58 4.6-4.58L18 10l-6 6-6-6 1.4-1.4z" /></svg>
            </span>
            {CK["entity_search.options.bin"]}
          </button>
          <div className={`ontario-hint-expander__content${hintExpanderOpen.bin ? ' ontario-expander__content--opened' : ''}`} id="hint-expander-content-bin" aria-labelledby="hint-expander-btn-bin" aria-hidden={!hintExpanderOpen.bin}>
            <ul dangerouslySetInnerHTML={{ __html: sanitizedHtml3 }} />
          </div>
        </div>
        <div className="search-input-group">
          <label className="search-label" htmlFor="ontario-search-input-field">
            {CK["entity_search.button.title"]}
          </label>
          <form onSubmit={handleOnSubmit} className="ontario-search__container" role="search" noValidate>
            {searchError && (
              <div className="ontario-error-messaging" role="alert">
                <span className="ontario-error-messaging__content">{searchError}</span>
              </div>
            )}
            <div className="ontario-search__input-container">
              <input
                type="search"
                name="search"
                id="ontario-search-input-field"
                autoComplete="off"
                aria-autocomplete="none"
                className={`ontario-search__input ontario-input${searchError ? ' ontario-input__error' : ''}`}
                required
                maxLength="100"
                value={searchText}
                onChange={handleOnChangeText}
                aria-label="Search Text"
              />
              <input
                type="reset"
                className="ontario-search__reset"
                id="ontario-search-reset"
                value=""
                aria-label="Clear search"
                onClick={handleReset}
              />
              <button
                type="submit"
                className="ontario-search__submit"
                id="ontario-search-box__submit"
              >
                <span className="ontario-show-for-sr">Submit</span>
                <svg className="svg-icon" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M15.5 14h-.8l-.28-.27A6.47 6.47 0 0 0 16 9.5a6.5 6.5 0 1 0-13 0A6.5 6.5 0 0 0 9.5 16a6.47 6.47 0 0 0 4.23-1.57l.27.28v.8l5 5 1.5-1.5-5-5zm-6 0C7 14 5 12 5 9.5S7 5 9.5 5 14 7 14 9.5 12 14 9.5 14z"/>
                </svg>
              </button>
            </div>
          </form>
        </div>
        <fieldset className="ontario-checkboxes">
          <div className="ontario-checkboxes__item">
            <input
              className="ontario-input ontario-checkboxes__input"
              name="exact_match"
              id="exact_match"
              type="checkbox"
              checked={exactMatch}
              onChange={handleExactMatchChange}
              tabIndex={0}
            />
            <label htmlFor="exact_match" className="ontario-checkboxes__label info-label">
              {CK["entity_search.exact_match"]}
            </label>
          </div>
        </fieldset>
        <div className="ontario-loading-indicator__container" aria-hidden="true">
          {isLoading && <LoadingIndicator />}
        </div>

        <div className='ontario-row' aria-hidden="true">
          {searchResultOpen && (
            <SearchResult
              results={dataResponse}
              totalRecords={dataResponse.length}
              pageSize={pageSize}
              setPageSize={setPageSize}
              displayInenigible={displayInenigible}
              setDisplayInenigible={setDisplayInenigible}
            />
          )}
        </div>
        
        {/* <Footer /> */}
      </div>
      <Footer />
    </div>
  );
}

export default Home;
